// Graph neighbor expansion API client.

import { getCsrfToken } from './api';
import type { SubgraphNode, SubgraphEdge } from '../types';

export interface NeighborExpansionResult {
  center_node_id: string;
  nodes: SubgraphNode[];
  edges: SubgraphEdge[];
}

/**
 * Expand neighbors for a given graph node via the FastAPI backend.
 */
export async function expandNeighbors(
  nodeId: string,
  nodeLabel: string,
  nodeName: string,
  maxNeighbors: number = 20,
  excludeDocuments: boolean = false,
  depth: number = 1,
  excludeLabels: string[] = [],
  includeLabels: string[] = [],
  dateFrom?: string,
  dateTo?: string,
): Promise<NeighborExpansionResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/neighbors', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      node_id: nodeId,
      node_label: nodeLabel,
      node_name: nodeName,
      max_neighbors: maxNeighbors,
      exclude_documents: excludeDocuments,
      depth,
      exclude_labels: excludeLabels,
      include_labels: includeLabels,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`neighbor expansion failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as NeighborExpansionResult;
}

// ── Seed Graph ────────────────────────────────────────────────────────────

export interface SeedGraphResult {
  nodes: (SubgraphNode & { score?: number })[];
  edges: SubgraphEdge[];
}

export async function seedGraph(
  name: string,
  label: string,
  maxEntities: number = 30,
  dateFrom?: string,
  dateTo?: string,
): Promise<SeedGraphResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/seed', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      name,
      label,
      max_entities: maxEntities,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`seed graph failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as SeedGraphResult;
}

// ── Smart View ────────────────────────────────────────────────────────────

export async function smartView(
  viewName: string,
  entityName: string,
  entityLabel: string,
): Promise<SeedGraphResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/smart-view', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      view_name: viewName,
      entity_name: entityName,
      entity_label: entityLabel,
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`smart view failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as SeedGraphResult;
}

// ── IOC Lookup ────────────────────────────────────────────────────────────

export interface IocLookupDocument {
  cite_key: string;
  title?: string | null;
  date?: string | null;
  url?: string | null;
  organization?: string | null;
}

export interface IocLookupEntity {
  name: string;
  label: string;
  malpedia_url?: string | null;
}

export interface CveDetails {
  cvss_score?: number | null;
  cvss_severity?: string | null;
  description?: string | null;
  cwe_ids?: string | null;
  published_date?: string | null;
  in_cisa_kev?: boolean | null;
}

export interface IocCategoryRef {
  key: string;     // "crypto.btc", "handle.telegram", "mitre.tactic"
  label: string;   // "Bitcoin", "Telegram", "Tactic"
  group: string;   // "crypto", "handles", "mitre"
}

export interface MentionStats {
  first_seen?: string | null;
  last_seen?: string | null;
  doc_count: number;
  family_count: number;
  actor_count: number;
}

export interface IocLookupResult {
  found: boolean;
  ioc_type?: string | null;
  ioc_value: string;
  documents: IocLookupDocument[];
  entities: IocLookupEntity[];
  cve_details?: CveDetails | null;
  category?: IocCategoryRef | null;
  mention_stats?: MentionStats | null;
}

const iocLookupCache = new Map<string, IocLookupResult>();

export async function lookupIoc(
  iocValue: string,
  iocType?: string,
): Promise<IocLookupResult> {
  const cacheKey = `${iocValue}::${iocType || ''}`;
  const cached = iocLookupCache.get(cacheKey);
  if (cached) return cached;

  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/ioc-lookup', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      ioc_value: iocValue,
      ...(iocType ? { ioc_type: iocType } : {}),
    }),
  });

  if (!resp.ok) {
    return { found: false, ioc_value: iocValue, documents: [], entities: [] };
  }

  const result = (await resp.json()) as IocLookupResult;
  iocLookupCache.set(cacheKey, result);
  return result;
}

// ── Actor Lookup ────────────────────────────────────────────────────────

export interface ActorFamilyRef {
  family_id: string;
  common_name: string;
  malpedia_url?: string | null;
}

export interface ActorTechniqueRef {
  attack_id: string;
  name: string;
}

export interface ActorDocumentRef {
  cite_key: string;
  title?: string | null;
  organization?: string | null;
  date?: string | null;
}

export interface ActorLookupResult {
  found: boolean;
  actor_id: string;
  common_name: string;
  aliases: string[];
  description?: string | null;
  country?: string | null;
  motivation?: string[] | null;
  malpedia_url?: string | null;
  families: ActorFamilyRef[];
  techniques: ActorTechniqueRef[];
  documents: ActorDocumentRef[];
  stats: {
    doc_count: number;
    family_count: number;
    technique_count: number;
  };
}

export async function lookupActor(name: string): Promise<ActorLookupResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/actor-lookup', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({ name }),
  });

  if (!resp.ok) {
    return {
      found: false,
      actor_id: '',
      common_name: name,
      aliases: [],
      families: [],
      techniques: [],
      documents: [],
      stats: { doc_count: 0, family_count: 0, technique_count: 0 },
    };
  }

  return (await resp.json()) as ActorLookupResult;
}

// ── Country Lookup ──────────────────────────────────────────────────────

export interface CountryActorRef {
  actor_id: string;
  common_name: string;
  country?: string | null;
  malpedia_url?: string | null;
  doc_count: number;
}

export interface CountryFamilyRef {
  family_id: string;
  common_name: string;
  malpedia_url?: string | null;
  actor_count: number;
}

export interface CountryTechniqueRef {
  attack_id: string;
  name: string;
}

export interface CountryDocumentRef {
  cite_key: string;
  title?: string | null;
  organization?: string | null;
  date?: string | null;
}

export interface CountryLookupResult {
  found: boolean;
  code: string;
  name: string;
  actor_count: number;
  family_count: number;
  technique_count: number;
  doc_count: number;
  actors: CountryActorRef[];
  top_families: CountryFamilyRef[];
  top_techniques: CountryTechniqueRef[];
  documents: CountryDocumentRef[];
}

export async function lookupCountry(code: string): Promise<CountryLookupResult> {
  const cc = code.toUpperCase();
  const resp = await fetch(`/api/fastapi/graph/country/${encodeURIComponent(cc)}`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!resp.ok) {
    return {
      found: false,
      code: cc,
      name: '',
      actor_count: 0,
      family_count: 0,
      technique_count: 0,
      doc_count: 0,
      actors: [],
      top_families: [],
      top_techniques: [],
      documents: [],
    };
  }
  return (await resp.json()) as CountryLookupResult;
}

// ── Family Lookup ───────────────────────────────────────────────────────

export interface FamilyActorRef {
  actor_id: string;
  common_name: string;
  malpedia_url?: string | null;
}

export interface FamilyTechniqueRef {
  attack_id: string;
  name: string;
}

export interface FamilyDocumentRef {
  cite_key: string;
  title?: string | null;
  organization?: string | null;
  date?: string | null;
}

export interface FamilyLookupResult {
  found: boolean;
  family_id: string;
  common_name: string;
  aliases: string[];
  description?: string | null;
  platform?: string | null;
  malpedia_url?: string | null;
  actors: FamilyActorRef[];
  techniques: FamilyTechniqueRef[];
  documents: FamilyDocumentRef[];
  stats: {
    doc_count: number;
    actor_count: number;
    technique_count: number;
  };
}

export async function lookupFamily(name: string): Promise<FamilyLookupResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/family-lookup', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({ name }),
  });

  if (!resp.ok) {
    return {
      found: false,
      family_id: '',
      common_name: name,
      aliases: [],
      actors: [],
      techniques: [],
      documents: [],
      stats: { doc_count: 0, actor_count: 0, technique_count: 0 },
    };
  }

  return (await resp.json()) as FamilyLookupResult;
}

// ── Technique Lookup ────────────────────────────────────────────────────

export interface TechniqueRef {
  attack_id: string;
  name: string;
}

export interface TechniqueActorRef {
  actor_id: string;
  common_name: string;
  malpedia_url?: string | null;
}

export interface TechniqueFamilyRef {
  family_id: string;
  common_name: string;
  malpedia_url?: string | null;
}

export interface TechniqueCveRef {
  value: string;
  cvss_score?: number | null;
  cvss_severity?: string | null;
}

export interface TechniqueDocumentRef {
  cite_key: string;
  title?: string | null;
  organization?: string | null;
  date?: string | null;
}

export interface TechniqueLookupResult {
  found: boolean;
  attack_id: string;
  name: string;
  description?: string | null;
  tactics: string[];
  is_subtechnique: boolean;
  parent?: TechniqueRef | null;
  subtechniques: TechniqueRef[];
  actors: TechniqueActorRef[];
  families: TechniqueFamilyRef[];
  cves: TechniqueCveRef[];
  documents: TechniqueDocumentRef[];
  stats: {
    doc_count: number;
    actor_count: number;
    family_count: number;
    cve_count: number;
  };
}

export async function lookupTechnique(attackId: string): Promise<TechniqueLookupResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/technique-lookup', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({ attack_id: attackId }),
  });

  if (!resp.ok) {
    return {
      found: false,
      attack_id: attackId,
      name: '',
      tactics: [],
      is_subtechnique: false,
      parent: null,
      subtechniques: [],
      actors: [],
      families: [],
      cves: [],
      documents: [],
      stats: { doc_count: 0, actor_count: 0, family_count: 0, cve_count: 0 },
    };
  }

  return (await resp.json()) as TechniqueLookupResult;
}

// ── Find Paths ──────────────────────────────────────────────────────────

export interface PathStep {
  node_id: string;
  node_label: string;
  node_name: string;
}

export interface SinglePath {
  nodes: PathStep[];
  edges: string[];   // rel types between consecutive nodes
  length: number;
}

export interface FindPathsMeta {
  path_count: number;
  shortest_length: number;
  used_fallback: boolean;
  message: string;
}

export interface FindPathsResult {
  paths: SinglePath[];
  subgraph: {
    nodes: (SubgraphNode & { score?: number })[];
    edges: SubgraphEdge[];
  };
  meta: FindPathsMeta;
}

export async function findPaths(
  sourceId: string,
  sourceLabel: string,
  sourceName: string,
  targetId: string,
  targetLabel: string,
  targetName: string,
  maxDepth: number = 5,
  maxPaths: number = 5,
  excludeLabels: string[] = ['Chunk', 'ChunkRun'],
  excludeInfrastructure: boolean = true,
  relTypes?: string[],
  dateFrom?: string,
  dateTo?: string,
): Promise<FindPathsResult> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/find-paths', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      source_id: sourceId,
      source_label: sourceLabel,
      source_name: sourceName,
      target_id: targetId,
      target_label: targetLabel,
      target_name: targetName,
      max_depth: maxDepth,
      max_paths: maxPaths,
      exclude_labels: excludeLabels,
      exclude_infrastructure: excludeInfrastructure,
      ...(relTypes?.length ? { rel_types: relTypes } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`find paths failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as FindPathsResult;
}

// ── Co-occurrence Graph ──────────────────────────────────────────────────

export interface CoOccurrenceNode {
  id: string;
  label: string;
  name: string;
  display_name?: string | null;
  doc_count: number;
  properties?: Record<string, unknown> | null;
}

export interface CoOccurrenceEdge {
  source: string;
  target: string;
  weight: number;
  breakdown: Record<string, number>;
}

export interface CoOccurrenceResponse {
  nodes: CoOccurrenceNode[];
  edges: CoOccurrenceEdge[];
  primary_label: string;
  stats: {
    total_nodes: number;
    connected_nodes: number;
    total_edges: number;
  };
}

export async function fetchCoOccurrence(
  primaryLabel: string = 'MalwareFamily',
  linkThrough: string[] = ['Document'],
  minWeight: number = 2,
  maxEdges: number = 2000,
  dateFrom?: string,
  dateTo?: string,
): Promise<CoOccurrenceResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/co-occurrence', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      primary_label: primaryLabel,
      link_through: linkThrough,
      min_weight: minWeight,
      max_edges: maxEdges,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`co-occurrence failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as CoOccurrenceResponse;
}

// ── Family ↔ Actor overview ──────────────────────────────────────────────

export interface FamilyActorEdge {
  source: string;            // family elementId
  target: string;            // actor elementId
  weight: number;            // total shared-evidence count
  breakdown: Record<string, number>;  // { Document: 3, IOC: 5 }
  malpedia_uses: boolean;    // curated USES edge present
}

export interface FamilyActorOverviewResponse {
  nodes: CoOccurrenceNode[];
  edges: FamilyActorEdge[];
  stats: {
    total_nodes: number;
    connected_nodes: number;
    total_edges: number;
  };
}

export async function fetchFamilyActorOverview(
  linkThrough: string[] = ['Document', 'IOC'],
  minWeight: number = 1,
  maxEdges: number = 2000,
  includeUses: boolean = true,
  dateFrom?: string,
  dateTo?: string,
): Promise<FamilyActorOverviewResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/family-actor-overview', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      link_through: linkThrough,
      min_weight: minWeight,
      max_edges: maxEdges,
      include_uses: includeUses,
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`family-actor overview failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as FamilyActorOverviewResponse;
}

// ── Edge provenance ──────────────────────────────────────────────────────

export interface EdgeProvenanceDocument {
  cite_key: string;
  title?: string | null;
  date?: string | null;
  url?: string | null;
  organization?: string | null;
}

export interface EdgeProvenanceIoc {
  value: string;
  type: string;
}

export interface EdgeProvenanceEntity {
  id: string;
  name?: string | null;
  display_name?: string | null;
}

export interface EdgeProvenanceResponse {
  documents: EdgeProvenanceDocument[];
  iocs: EdgeProvenanceIoc[];
  malpedia_uses: boolean;
  family?: EdgeProvenanceEntity | null;
  actor?: EdgeProvenanceEntity | null;
}

export async function fetchEdgeProvenance(
  sourceId: string,
  targetId: string,
  linkTypes: string[] = ['Document', 'IOC'],
  maxPerType: number = 50,
): Promise<EdgeProvenanceResponse> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/edge-provenance', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({
      source_id: sourceId,
      target_id: targetId,
      link_types: linkTypes,
      max_per_type: maxPerType,
    }),
  });

  if (!resp.ok) {
    const errText = await resp.text().catch(() => resp.statusText);
    throw new Error(`edge provenance failed: ${resp.status} ${errText || ''}`.trim());
  }

  return (await resp.json()) as EdgeProvenanceResponse;
}

// ── Overview temporal (sparkline strip) ─────────────────────────────────

export interface OverviewTemporalPoint {
  ym: string;
  n: number;
}

export interface OverviewTemporalPartner {
  id: string;
  name: string;
  display_name: string;
  label: string;
  monthly: OverviewTemporalPoint[];
}

export interface OverviewTemporalRow {
  id: string;
  name: string;
  display_name: string;
  label: string;
  total: number;
  monthly: OverviewTemporalPoint[];
  top_partners: OverviewTemporalPartner[];
}

export interface OverviewTemporalResponse {
  primary_label: string;
  rows: OverviewTemporalRow[];
  built_at: string;
}

export async function fetchOverviewTemporal(
  primary: 'MalwareFamily' | 'ThreatActor' = 'MalwareFamily',
  topN: number = 30,
  dateFrom?: string,
  dateTo?: string,
): Promise<OverviewTemporalResponse> {
  const params = new URLSearchParams({
    primary,
    top_n: String(topN),
    ...(dateFrom ? { date_from: dateFrom } : {}),
    ...(dateTo ? { date_to: dateTo } : {}),
  });
  const resp = await fetch(`/api/fastapi/graph/overview-temporal?${params.toString()}`, {
    method: 'GET',
    credentials: 'same-origin',
  });
  if (!resp.ok) {
    return { primary_label: primary, rows: [], built_at: '' };
  }
  return (await resp.json()) as OverviewTemporalResponse;
}

// ── Co-occurring IOCs ────────────────────────────────────────────────────

export interface CoOccurringIocItem {
  ioc_value: string;
  ioc_type: string;
  co_occurrence_count: number;
}

export async function lookupCoOccurringIocs(
  iocValue: string,
  limit: number = 30,
): Promise<CoOccurringIocItem[]> {
  const csrf = await getCsrfToken();
  const resp = await fetch('/api/fastapi/graph/ioc-cooccurring', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRFToken': csrf,
    },
    body: JSON.stringify({ ioc_value: iocValue, limit }),
  });

  if (!resp.ok) return [];
  const data = (await resp.json()) as { co_occurring: CoOccurringIocItem[] };
  return data.co_occurring || [];
}
